import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Truck, MapPin, Clock, Phone, ChefHat, Star, Plus, Minus,
  ShoppingBag, Award, Heart, Calendar
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sort categories the way the chef wants them displayed
const CATEGORY_ORDER = ["Sandwich", "Bowl", "Side", "Drink"];
const sortByCategory = (items) =>
  [...items].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a.category);
    const bi = CATEGORY_ORDER.indexOf(b.category);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

// Hero image — Chef Eric's food truck (uploaded via admin)
const HERO_IMAGE = "/images/food-truck.jpg";

export const FoodTruck = () => {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cart specific to food-truck orders, stored separately from meal-prep cart
  const [truckCart, setTruckCart] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [menuRes, scheduleRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/menu-items?menu_type=food_truck&available=true`),
          axios.get(`${BACKEND_URL}/api/food-truck/schedule?upcoming_only=true`),
        ]);
        setMenuItems(sortByCategory(menuRes.data));
        setSchedule(scheduleRes.data);
      } catch (e) {
        console.error("Failed to load food truck data", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const savedCart = localStorage.getItem("food_truck_cart");
    if (savedCart) {
      try {
        setTruckCart(JSON.parse(savedCart));
      } catch { /* ignore */ }
    }
  }, []);

  // Sync to the shared cart used by Navbar count + Cart page.
  // We mirror our food-truck cart into the `cart` localStorage key with a
  // `fulfillment_type` marker so checkout knows what to do.
  useEffect(() => {
    localStorage.setItem("food_truck_cart", JSON.stringify(truckCart));
    // Replace shared cart with food-truck items + flag
    const sharedCart = truckCart.map((it) => ({
      menu_item_id: it.id,
      name: it.name,
      price: it.price,
      quantity: it.quantity,
      image: it.image || "",
      extras: [],
      fulfillment_type: "food_truck_pickup",
    }));
    if (truckCart.length > 0) {
      localStorage.setItem("cart", JSON.stringify(sharedCart));
    }
  }, [truckCart]);

  const updateQty = (item, delta) => {
    setTruckCart((prev) => {
      const existing = prev.find((c) => c.id === item.id);
      if (!existing) {
        if (delta < 0) return prev;
        return [...prev, { ...item, quantity: 1 }];
      }
      const newQty = existing.quantity + delta;
      if (newQty <= 0) return prev.filter((c) => c.id !== item.id);
      return prev.map((c) => (c.id === item.id ? { ...c, quantity: newQty } : c));
    });
  };

  const getQty = (itemId) =>
    truckCart.find((c) => c.id === itemId)?.quantity || 0;

  const cartSubtotal = useMemo(
    () => truckCart.reduce((s, it) => s + it.price * it.quantity, 0),
    [truckCart]
  );
  const cartCount = useMemo(
    () => truckCart.reduce((s, it) => s + it.quantity, 0),
    [truckCart]
  );

  const goToCheckout = () => {
    if (cartCount === 0) {
      toast.error("Add at least one item to pre-order");
      return;
    }
    if (schedule.length === 0) {
      toast.error("No upcoming food-truck stops yet — please call to pre-order.");
      return;
    }
    navigate("/cart");
  };

  // Group menu items by category for clean display
  const grouped = useMemo(() => {
    const out = {};
    menuItems.forEach((it) => {
      const cat = it.category || "Other";
      if (!out[cat]) out[cat] = [];
      out[cat].push(it);
    });
    return out;
  }, [menuItems]);

  return (
    <div className="min-h-screen" data-testid="food-truck-home">
      {/* Hero — full bleed, image right, content left */}
      <section className="relative pt-28 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-stone-900 via-emerald-900 to-stone-900">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3 space-y-6 text-white">
              <div className="flex items-center gap-2">
                <Badge className="bg-amber-400 text-amber-900 hover:bg-amber-400 border-amber-400">
                  <Truck className="h-3.5 w-3.5 mr-1" /> Now Rolling
                </Badge>
                <Badge className="bg-white/10 text-white hover:bg-white/10 border-white/20">
                  Fort Worth, TX
                </Badge>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Raffy's Cuisine
                <span className="block text-emerald-300">Food Truck</span>
              </h1>
              <p className="text-lg text-stone-200 max-w-xl leading-relaxed">
                Chef Eric's signature flavors — served hot off the truck. Pre-order online
                and skip the line, or stop by the window when you spot us.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#truck-menu">
                  <Button
                    size="lg"
                    data-testid="hero-order-cta"
                    className="bg-emerald-500 hover:bg-emerald-400 text-white text-base px-8 py-6 shadow-xl"
                  >
                    <ShoppingBag className="h-5 w-5 mr-2" /> Pre-Order Online
                  </Button>
                </a>
                <a href="#truck-schedule">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-stone-900 text-base px-8 py-6"
                  >
                    <MapPin className="h-5 w-5 mr-2" /> Find Us
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-6 pt-2 text-sm text-stone-300">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                  <span>Chef-driven menu</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-emerald-300" />
                  <span>Made fresh daily</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-br from-emerald-400/30 to-amber-400/30 rounded-3xl blur-2xl" />
                <img
                  src={HERO_IMAGE}
                  alt="Raffy's Cuisine Food Truck"
                  className="relative w-full h-72 sm:h-96 object-cover rounded-3xl shadow-2xl ring-1 ring-white/10"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props strip */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-b border-stone-100">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: ChefHat, title: "Chef Eric, on the truck", text: "Every order made by the chef himself or trained crew." },
            { icon: Heart, title: "Skip the line", text: "Pre-order online and we'll have it hot and ready when you arrive." },
            { icon: Calendar, title: "Find us all over", text: "Events, office lunches, and weekly stops across the Metroplex." },
          ].map((v) => (
            <div key={v.title} className="flex items-start gap-4">
              <div className="bg-emerald-50 rounded-xl p-3 flex-shrink-0">
                <v.icon className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Schedule */}
      <section id="truck-schedule" className="py-16 px-4 sm:px-6 lg:px-8 bg-stone-50 scroll-mt-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-3">Find Us</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Upcoming Stops</h2>
            <p className="text-gray-600 mt-2">Click a stop's "Pre-Order" button to reserve your food.</p>
          </div>

          {loading ? (
            <p className="text-center text-gray-500">Loading schedule…</p>
          ) : schedule.length === 0 ? (
            <Card className="border-2 border-dashed border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="food-truck-no-schedule">
              <CardContent className="p-10 sm:p-12 text-center">
                <div className="bg-emerald-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Truck className="h-8 w-8 text-emerald-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Schedule Coming Soon</h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Chef Eric is finalizing this week's stops. Give us a call to catch us at
                  private events or book the truck for your own gathering.
                </p>
                <a href="tel:+16824023734">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Phone className="h-4 w-4 mr-2" /> Call (682) 402-3734
                  </Button>
                </a>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {schedule.map((stop) => {
                const dt = new Date(stop.event_date);
                return (
                  <Card
                    key={stop.id}
                    data-testid={`food-truck-stop-${stop.id}`}
                    className="border-2 hover:border-emerald-300 transition-all hover:shadow-lg"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="bg-emerald-50 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">{stop.location}</h3>
                          {stop.address && (
                            <p className="text-sm text-gray-500 mt-0.5">{stop.address}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700 mb-3">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">
                          {dt.toLocaleDateString("en-US", {
                            weekday: "long",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                        <span className="text-gray-500">• {stop.start_time} – {stop.end_time}</span>
                      </div>
                      {stop.notes && (
                        <p className="text-sm text-gray-600 italic mb-3">{stop.notes}</p>
                      )}
                      <a href="#truck-menu">
                        <Button
                          size="sm"
                          className="w-full bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`stop-preorder-${stop.id}`}
                        >
                          Pre-Order for This Stop
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Menu — ordered: Sandwiches → Drinks → Sides */}
      <section id="truck-menu" className="py-16 px-4 sm:px-6 lg:px-8 bg-white scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-orange-100 text-orange-800 border-orange-200 mb-3">
              <ChefHat className="h-4 w-4 mr-1" /> On the Truck
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">Food Truck Menu</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Pre-order for pickup or order from the window when you see us — handed hot off the truck.
            </p>
          </div>

          {loading ? (
            <p className="text-center text-gray-500">Loading menu…</p>
          ) : menuItems.length === 0 ? (
            <p className="text-center text-gray-500" data-testid="food-truck-empty-menu">
              The food-truck menu is being updated. Check back soon!
            </p>
          ) : (
            <div className="space-y-10">
              {CATEGORY_ORDER.filter((cat) => grouped[cat]?.length).map((cat) => (
                <div key={cat}>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    {cat === "Sandwich" && "Sandwiches"}
                    {cat === "Bowl" && "Bowls"}
                    {cat === "Drink" && "Drinks"}
                    {cat === "Side" && "Sides"}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {grouped[cat].map((item) => {
                      const qty = getQty(item.id);
                      return (
                        <Card
                          key={item.id}
                          data-testid={`food-truck-item-${item.id}`}
                          className="border-2 hover:border-emerald-200 transition-all"
                        >
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-lg font-bold text-gray-900">{item.name}</h4>
                              <span className="text-emerald-600 font-bold text-lg whitespace-nowrap ml-2">
                                ${item.price.toFixed(2)}
                              </span>
                            </div>
                            {item.description && (
                              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                            )}
                            {qty === 0 ? (
                              <Button
                                onClick={() => updateQty(item, +1)}
                                data-testid={`add-${item.id}`}
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                              >
                                <Plus className="h-4 w-4 mr-1" /> Add to Pre-Order
                              </Button>
                            ) : (
                              <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateQty(item, -1)}
                                  data-testid={`decrease-${item.id}`}
                                  className="h-8 w-8 p-0"
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                                <span className="font-bold text-gray-900" data-testid={`qty-${item.id}`}>
                                  {qty}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateQty(item, +1)}
                                  data-testid={`increase-${item.id}`}
                                  className="h-8 w-8 p-0"
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Sticky bottom checkout bar when cart has items */}
      {cartCount > 0 && (
        <div
          data-testid="food-truck-cart-bar"
          className="fixed bottom-0 inset-x-0 z-40 bg-white border-t-2 border-emerald-600 shadow-2xl"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-600 text-white rounded-full h-10 w-10 flex items-center justify-center font-bold">
                {cartCount}
              </div>
              <div>
                <p className="text-xs text-gray-500">Pre-Order Total</p>
                <p className="font-bold text-lg text-gray-900">${cartSubtotal.toFixed(2)}</p>
              </div>
            </div>
            <Button
              onClick={goToCheckout}
              data-testid="food-truck-checkout-cta"
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Checkout
            </Button>
          </div>
        </div>
      )}

      {/* Contact strip */}
      <section id="contact" className="py-12 px-4 bg-emerald-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Book the Truck for Your Event</h2>
          <p className="text-emerald-50 mb-5">Private parties, corporate lunches, weddings — Chef Eric brings the flavor.</p>
          <a href="tel:+16824023734">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-gray-100 font-semibold">
              <Phone className="h-5 w-5 mr-2" /> Call (682) 402-3734
            </Button>
          </a>
          <p className="mt-6 text-sm text-emerald-100">
            Looking for weekly meals? <Link to="/meal-prep" className="underline font-semibold">Visit our Meal Prep menu →</Link>
          </p>
        </div>
      </section>
    </div>
  );
};
